<template>
  <v-app>
    <v-main>
      <logout class="noprint" />
      <v-container v-if="loading" fill-height grid-list-md text-center ghost>
        <v-row wrap align-center>
          <v-col>
            Computation progress...
            <v-progress-linear
              color="light-blue accent-4"
              indeterminate
              rounded
              height="6"
            ></v-progress-linear>
          </v-col>
        </v-row>
      </v-container>
      <v-container ghost v-else>
        <iol-result-header :fields="results_header" />
        <v-sheet
          :class="currentSide ? 'iol-right-v-sheet' : 'iol-left-v-sheet'"
        >
          <h2 :align="currentSide ? 'left' : 'right'">
            {{ currentSide ? 'OD' : 'OS' }}
          </h2>
          <v-row>
            <v-col class="col-12 col-md-6" :order-sm="currentSide ? '2' : '1'">
              <iol-result-used-param :fields="common_inputs" />
              <iol-result-corn-ref-surg :fields="corneal_history" />
              <iol-result-switch-history :fields="switch_hist" />
            </v-col>
            <v-col class="col-12 col-md-6" :order-sm="currentSide ? '1' : '2'">
              <iol-data-table-power :fields="results_table" />
            </v-col>
          </v-row>
        </v-sheet>
        <div class="noprint">
          <v-row>
            <v-col cols="12" class="text-center gray">
              <a @click="print" :style="resetstyle">Print</a>
            </v-col>
          </v-row>
          <br />
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn to="/complex" color="#3B4256" class="white--text" x-large
                >Modify Calculation</v-btn
              >
            </v-col>
            <v-col cols="12" class="text-center">
              <v-btn
                @click="submit_toric"
                color="#3B4256"
                class="white--text"
                :disabled="results_table.selected_power == null"
                x-large
              >
                Toric Calculation
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import Logout from '@/vue/Logout.vue'
import IolDataTablePower from '@/components/results/IolDataTablePower.vue'
import IolResultCornRefSurg from '@/components/results/IolResultCornRefSurg.vue'
import IolResultHeader from '@/components/results/IolResultHeader.vue'
import IolResultUsedParam from '@/components/results/IolResultUsedParam.vue'
import IolSwitchField from '@/components/elementary/IolSwitchField.vue'
import IolResultSwitchHistory from '@/components/results/IolResultSwitchHistory.vue'

export default {
  name: 'Result',
  components: {
    Logout,
    IolDataTablePower,
    IolResultCornRefSurg,
    IolResultHeader,
    IolResultUsedParam,
    IolSwitchField,
    IolResultSwitchHistory,
  },
  data() {
    return {
      resetstyle: { color: '#838383' },
      loading: true,
      results_header: {
        title: 'PEARL-DGS - Results - Complex Eyes',
        patientID: {
          label: 'Patient ID / Name',
          readonly: true,
          tooltip: '',
          text: '',
        },
        keratometric_index: {
          label: 'Keratometric Index',
          readonly: true,
          tooltip: '',
          text: '',
        },
        biometer: {
          label: 'biometer',
          readonly: true,
          tooltip: '',
          text: '',
        },
      },
      common_inputs: {
        al: '',
        k1: '',
        k2: '',
        acd: '',
        aconst_title: 'A Constant',
        aconst: '',
        target_title: 'Target refraction',
        target: '',
        lt: '',
        cct: '',
        total_corneal_power_measurement_title: '',
        total_corneal_power_measurement_unit: '',
        total_corneal_power_measurement: '',
        inputs_type: 'complex',
      },
      switch_hist: {
        is_rk: false,
        is_icl: false,
        is_non_physio_cornea: false,
      },
      corneal_history: {
        is_corneal_laser: false,
        corneal_laser_type: '',
        pre_lvc_arc: '',
        laser_amount: '',
      },
      results_table: {
        selected: [],
        selected_power: null,
        selected_es: null,
        headers: [
          { text: 'IOL POWER (D)', value: 'power' },
          { text: 'REFRACTION (D)', value: 'es' },
        ],
        powers: [
          {
            id: 0,
            name: 'pm1',
            es: 'espm1',
            power: 'powerpm1',
          },
          {
            id: 1,
            name: 'pm05',
            es: 'espm05',
            power: 'powerpm05',
          },
          {
            id: 2,
            name: 'p0',
            es: 'esp0',
            power: 'powerp0',
          },
          {
            id: 3,
            name: 'p05',
            es: 'esp05',
            power: 'powerp05',
          },
          {
            id: 4,
            name: 'p1',
            es: 'esp1',
            power: 'powerp1',
          },
        ],
      },
    }
  },
  mounted() {
    setTimeout(() => {
      this.loading = false
    }, 2000)

    const inputs = this.currentInputs
    const results = this.currentResults

    // init patient/kera/biom
    this.results_header.patientID.text = inputs.patientID
    this.results_header.keratometric_index.text = inputs.keratometric_index
    this.results_header.biometer.text = inputs.biometer.text

    // init common inputs
    this.common_inputs.al = inputs.al
    this.common_inputs.k1 = inputs.k1
    this.common_inputs.k2 = inputs.k2
    this.common_inputs.acd = inputs.acd
    this.common_inputs.aconst = inputs.aconst
    this.common_inputs.target = inputs.target
    this.common_inputs.lt = inputs.lt
    this.common_inputs.cct = inputs.cct
    if (inputs.prc_topo) {
      this.common_inputs.total_corneal_power_measurement = inputs.prc_topo
      this.common_inputs.total_corneal_power_measurement_title = 'PRC_topo'
      this.common_inputs.total_corneal_power_measurement_unit = 'mm'
    } else if (inputs.tk) {
      this.common_inputs.total_corneal_power_measurement = inputs.tk
      this.common_inputs.total_corneal_power_measurement_title = 'TK'
      this.common_inputs.total_corneal_power_measurement_unit = 'D'
    } else if (inputs.tcrp) {
      this.common_inputs.total_corneal_power_measurement = inputs.tcrp
      this.common_inputs.total_corneal_power_measurement_title = 'TCRP'
      this.common_inputs.total_corneal_power_measurement_unit = 'D'
    }

    // init rk/icl/non_physio_cornea
    this.switch_hist.is_icl = inputs.is_icl
    this.switch_hist.is_rk = inputs.is_rk
    this.switch_hist.is_non_physio_cornea = inputs.is_non_physio_cornea

    // init corneal history
    this.corneal_history.is_corneal_laser = inputs.is_corneal_laser
    this.corneal_history.corneal_laser_type = inputs.corneal_laser_type.text
    this.corneal_history.pre_lvc_arc = inputs.pre_lvc_arc
    this.corneal_history.laser_amount = inputs.laser_amount

    // init result table
    this.results_table.powers.forEach((power) => {
      const resultData = results[power.name]
      if (resultData) {
        power.es = resultData.es
        power.power = resultData.power
      }
    })
  },
  computed: {
    ...mapState(['results', 'results2', 'ginputs', 'ginputs2', 'appHeader', 'currentSide']),
    // Get the correct inputs based on which eye was calculated
    currentInputs() {
      return this.currentSide ? this.ginputs : this.ginputs2
    },
    // Get the correct results based on which eye was calculated
    currentResults() {
      return this.currentSide ? this.results : this.results2
    },
  },
  methods: {
    print() {
      window.print()
    },
    submit_toric() {
      this.$store.dispatch('updateInputsToric', this.results_table)
      this.$store.dispatch('updateToricCalcAllowed', true)
      this.$router.push('toric')
    },
    ...mapActions(['updateInputsToric', 'setAuthUser']),
  },
}
</script>
